import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Database, LineChart, Settings, FileCheck } from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    title: "Existing Data Sources",
    description: "Use your existing video feeds, sensors and operational data. No requirement to install new infrastructure where existing coverage is adequate.",
    icon: Database,
  },
  {
    title: "Defined Outputs",
    description: "Convert observations into specific metrics, alerts or workflow triggers. Know exactly what the system will deliver before deployment.",
    icon: LineChart,
  },
  {
    title: "Data Governance",
    description: "Clear data governance, retention policies and integration requirements defined upfront. No ambiguity about data handling.",
    icon: Settings,
  },
  {
    title: "Bounded Pilots",
    description: "Start with a bounded pilot with clear deliverables and conversion criteria. Scale only when value is demonstrated.",
    icon: FileCheck,
  },
];

const useCases = [
  {
    sector: "Transport",
    outcomes: ["Junction performance metrics", "Flow anomaly detection", "Incident alerting"],
  },
  {
    sector: "Retail & Commercial",
    outcomes: ["Footfall analytics", "Queue monitoring", "Occupancy management"],
  },
  {
    sector: "Public Spaces",
    outcomes: ["Crowd density monitoring", "Safety threshold alerts", "Event management support"],
  },
];

export default function CivilIntelligencePage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/solutions" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Solutions
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Civil Operational Intelligence
            </h1>
            <p className="text-subtitle max-w-2xl mb-4">
              Analytics from existing infrastructure through UrbanAnalytix
            </p>
            <p className="text-body max-w-2xl">
              UrbanAnalytix delivers operational intelligence from your existing video and sensor infrastructure. Convert observations into defined metrics, alerts and workflow outputs—without broad "smart city" promises.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Positioning */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              Decisions Improved, Not Technology Deployed
            </h2>
            <p className="text-body mb-4">
              The value of operational analytics lies in better decisions, not in the technology itself. UrbanAnalytix focuses on defined use cases with measurable operational outcomes.
            </p>
            <p className="text-body">
              We start with your existing data sources and the decisions you need to support. The output is specific metrics, alerts or workflow triggers—not generic dashboards.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Approach</p>
            <h2 className="text-headline text-foreground">How UrbanAnalytix Works</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <feature.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Applications</p>
            <h2 className="text-headline text-foreground">Sector Examples</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                className="card-pillar p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-label text-foreground mb-4">{useCase.sector}</h3>
                <ul className="space-y-2">
                  {useCase.outcomes.map((outcome, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{outcome}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Scope an Analytics Pilot
            </h2>
            <p className="text-body mb-8">
              Tell us about your data sources, the decisions you need to support, and the outputs you require. We will scope a bounded pilot with clear deliverables.
            </p>
            <Link to="/contact?type=analytics" className="btn-primary">
              Discuss Your Requirements
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
